import { ApiProject, ApiSimpleTenant } from "@incendium/api";
import { Check, Delete, Edit } from "@mui/icons-material";
import {
  Button,
  IconButton,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import Loading from "Components/Loading/Loading";
import SpacedList from "Components/UI/SpacedList";
import { cell3Icons } from "consts";
import useAllProjects from "features/project/hooks/useAllProjects";
import {
  deleteSamlTenant,
  getSamlTenantProjectLinks,
} from "features/sso/samlService";
import SamlAdminSetupDialog from "features/sso/components/SamlAdminSetupDialog";
import SamlClientSetupDialog from "features/sso/components/SamlClientSetupDialog";
import { formatEnumVal } from "Helpers/enumToText";
import { useSamlTenants } from "Hooks/useSamlTenants";
import { useConfirmation } from "Hooks/useConfirmation";
import produce from "immer";

function formatTenantProjectsLabel(
  tenant: ApiSimpleTenant,
  allProjects: ApiProject[],
): string {
  const links = getSamlTenantProjectLinks(tenant);
  if (links.length === 0) return "—";
  return links
    .map((l) => {
      const p = allProjects.find(
        (x) => x.id === l.projectId && x.clientId === l.clientId,
      );
      return p?.name || `#${l.projectId}`;
    })
    .join(", ");
}

export interface SamlTenantsListProps {
  onAddClick: () => void;
  onEditClick: (tenant: ApiSimpleTenant) => void;
}

function SamlTenantsList({ onAddClick, onEditClick }: SamlTenantsListProps) {
  const { tenants, setTenants, loading } = useSamlTenants();
  const { allProjects } = useAllProjects();
  const handleDelete = useConfirmation();
  const onDelete = (tenant: ApiSimpleTenant) => {
    const internalId = tenant.id;
    if (!internalId) return;
    handleDelete({
      title: "Delete SSO tenant?",
      body: "This removes SAML mapping for this enterprise app. Users may lose SSO access.",
      callback: async () => {
        await deleteSamlTenant(internalId);
        setTenants(
          produce(tenants, (draft) => {
            const idx = draft.findIndex((t) => t.id === internalId);
            if (idx >= 0) draft.splice(idx, 1);
          }),
        );
        return "SSO tenant removed";
      },
    });
  };

  if (loading && tenants.length === 0) {
    return <Loading fullHeight />;
  }

  return (
    <SpacedList
      title="SSO tenants"
      action={
        <Stack direction="row" spacing={1}>
          <SamlAdminSetupDialog />
          <Button onClick={onAddClick}>Add SSO tenant</Button>
        </Stack>
      }
    >
      <TableHead>
        <TableRow>
          <TableCell>Entity ID</TableCell>
          <TableCell>Domain</TableCell>
          <TableCell>Metadata URL</TableCell>
          <TableCell>Name</TableCell>
          <TableCell>Projects</TableCell>
          <TableCell>Provider</TableCell>
          <TableCell>Enabled</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {tenants.map((t) => (
          <TableRow key={t.id || t.entityId}>
            <TableCell className="primary">{t.entityId}</TableCell>
            <TableCell>{t.domain}</TableCell>
            <TableCell
              sx={{
                maxWidth: 200,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={t.metaDataUrl}
            >
              {t.metaDataUrl || "—"}
            </TableCell>
            <TableCell>{t.name}</TableCell>
            <TableCell sx={{ maxWidth: 280 }}>
              {formatTenantProjectsLabel(t, allProjects)}
            </TableCell>
            <TableCell>
              {t.provider ? formatEnumVal(t.provider) : "—"}
            </TableCell>
            <TableCell>
              {t.enabled ? <Check fontSize="small" color="success" /> : "No"}
            </TableCell>
            <TableCell style={{ width: cell3Icons }}>
              <IconButton
                size="small"
                onClick={() => t.id && onEditClick(t)}
                disabled={!t.id}
              >
                <Edit fontSize="small" />
              </IconButton>
              <SamlClientSetupDialog tenant={t} />
              <IconButton size="small" onClick={() => onDelete(t)}>
                <Delete fontSize="small" />
              </IconButton>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </SpacedList>
  );
}

export default SamlTenantsList;
